import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogDescription,
  Input,
} from '@you/ui'
import { Plus } from 'lucide-react'
import { useRef, useState, useEffect } from 'react'
import { useUpdateWorkspace } from '@/api'
import { Workspace } from '@/api/types/workspace.types'
import { Icon } from '@/components/Icon'

/**
 * 生成随机的DiceBear头像URL
 * @param seed 可选的种子值，如果不提供会生成随机种子
 * @returns DiceBear头像URL
 */
export const generateAvatarUrl = (seed?: string): string => {
  const randomSeed = seed || Math.random().toString(36).substring(2, 15)
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${randomSeed}`
}

interface UpdateSpaceProps {
  mode?: 'create' | 'edit'
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
  successCallback: (data: unknown) => void
  closeDialog: () => void
  workspace?: Workspace // 编辑模式时传入的工作空间数据
  showTrigger?: boolean // 是否显示触发按钮
  disableName?: boolean // 禁用名称修改（组织空间）
}

export function UpdateSpace({
  mode = 'create',
  dialogOpen,
  setDialogOpen,
  successCallback,
  closeDialog,
  workspace,
  showTrigger = false,
  disableName = false,
}: UpdateSpaceProps) {
  const [avatar, setAvatar] = useState<string>('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isAvatarLoading, setIsAvatarLoading] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { trigger: updateWorkspace } = useUpdateWorkspace()

  // 初始化表单数据
  useEffect(() => {
    if (dialogOpen) {
      if (mode === 'edit' && workspace) {
        setAvatar(workspace.icon || generateAvatarUrl())
        setName(workspace.name)
      } else {
        // 新增模式，生成随机头像
        setAvatar(generateAvatarUrl())
        setName('')
      }
      setError('')
    }
  }, [dialogOpen, mode, workspace])

  // 修改头像
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (ev) => {
        setAvatar(ev.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // 生成新的随机头像
  const handleGenerateRandomAvatar = () => {
    setIsAvatarLoading(true)
    const newAvatarUrl = generateAvatarUrl()

    // 创建一个新的Image对象来预加载图片
    const img = new Image()
    img.onload = () => {
      setAvatar(newAvatarUrl)
      setIsAvatarLoading(false)
    }
    img.onerror = () => {
      setIsAvatarLoading(false)
      // 如果加载失败，可以设置一个默认头像或显示错误提示
    }
    img.src = newAvatarUrl
  }

  // 保存设置
  const handleSubmit = async () => {
    if (!disableName && !name.trim()) {
      setError('空间名称不能为空')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      const res = await updateWorkspace({
        name,
        icon: avatar, // 现在总是有值，要么是用户上传的，要么是DiceBear生成的
        id: workspace?.id,
      })
      successCallback(res)
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : `${mode === 'create' ? '创建' : '更新'}空间失败`
      setError(errorMessage)
      console.error(`${mode === 'create' ? '创建' : '更新'}空间失败:`, err)
    } finally {
      setIsLoading(false)
    }
  }

  const isCreateMode = mode === 'create'
  const dialogTitle = isCreateMode ? '新增工作空间' : '编辑工作空间'
  const submitButtonText = isLoading
    ? isCreateMode
      ? '创建中...'
      : '保存中...'
    : isCreateMode
      ? '确定'
      : '保存'

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button variant="link" className="w-full text-lg h-[48px]">
            <Plus />
            <p className="mx-2">新增空间</p>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader title={dialogTitle} />
        <DialogDescription className="px-6">
          {isCreateMode
            ? '创建一个新的工作空间，用于组织和管理您的内容。'
            : '编辑当前工作空间的基本信息。'}
        </DialogDescription>
        <div className="flex flex-col items-center px-6 pb-4">
          <div className="mb-4 flex flex-col items-center">
            <div className="relative">
              <div
                className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border border-dashed border-gray-300"
                // onClick={() => fileInputRef.current?.click()}
              >
                {isAvatarLoading ? (
                  <div className="w-16 h-16 flex items-center justify-center">
                    <div className="flex items-center space-x-1">
                      <div
                        className="w-1.5 h-1.5 bg-[#8752ff] rounded-full animate-bounce"
                        style={{ animationDelay: '0s' }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 bg-[#8752ff] rounded-full animate-bounce"
                        style={{ animationDelay: '0.1s' }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 bg-[#8752ff] rounded-full animate-bounce"
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={avatar}
                    alt="avatar"
                    className="w-16 h-16 object-cover"
                  />
                )}
              </div>
              <button
                type="button"
                onClick={handleGenerateRandomAvatar}
                disabled={isAvatarLoading}
                className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full text-xs flex items-center justify-center transition-colors ${
                  isAvatarLoading
                    ? 'bg-gray-200 cursor-not-allowed'
                    : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
                }`}
                title={isAvatarLoading ? '生成中...' : '生成随机头像'}
              >
                <Icon name="stars" size={22} />
              </button>
            </div>
            <p className="flex items-center text-xs text-gray-500 mt-2">
              点击
              <Icon name="stars" size={22} />
              生成随机头像
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>
          <div className="mb-2 text-left w-full">
            <label className="mb-1 text-base font-normal flex items-center">
              <span className="text-red-500 mr-1">*</span>名称
            </label>
            <Input
              error={!!error && !disableName}
              errorMessage={!disableName ? error : undefined}
              placeholder="请输入空间名称"
              value={name}
              disabled={disableName}
              onChange={(e) => {
                if (disableName) return
                setName(e.target.value)
                if (error) setError('')
              }}
            />
            {disableName && (
              <p className="mt-1 text-xs text-gray-400">
                （如需修改请联系管理员）
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={closeDialog}
            disabled={isLoading}
          >
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {submitButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// 便捷的创建模式组件
export function AddSpace(props: Omit<UpdateSpaceProps, 'mode' | 'workspace'>) {
  return <UpdateSpace {...props} mode="create" showTrigger={true} />
}
